import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, BookOpen, Crown, Shield, Compass, Star, Users } from 'lucide-react';
import { UnifiedHeader } from '../components/layout';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';

interface Officer {
  name: string;
  title: string;
  description: string;
  image?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const officers: Officer[] = [
  {
    name: "Krangar Davis",
    title: "President",
    description: "Leading our family association with vision and dedication to preserving our heritage.",
    image: "/images/officer-1.jpeg",
    icon: Crown,
    color: "brand"
  },
  {
    name: "Faustinus Davis",
    title: "Vice President",
    description: "Supporting our mission and ensuring continuity in our family initiatives.",
    image: "/images/officer-2.png",
    icon: Shield,
    color: "brand"
  },
  {
    name: "Chris Eggley",
    title: "Treasurer",
    description: "Managing our family's financial resources with transparency and accountability.",
    image: "/images/officer-3.png",
    icon: Star,
    color: "brand"
  },
  {
    name: "Janice Dampare",
    title: "Secretary",
    description: "Maintaining our records and ensuring effective communication across the family.",
    image: "/images/officer-4.jpeg",
    icon: BookOpen,
    color: "brand"
  },
  {
    name: "Massa Warner",
    title: "Financial Secretary",
    description: "Overseeing financial operations and member contributions with diligence.",
    image: "/images/officer-5.jpeg",
    icon: Compass,
    color: "brand"
  },
  {
    name: "Roberta Hansen",
    title: "Chaplain",
    description: "Providing spiritual guidance and upholding our motto: 'A family that prays together stays together.'",
    image: "/images/officer-6.png",
    icon: Heart,
    color: "brand"
  }
];

const MeetTheOfficersPage: React.FC = () => {
  return (
    <div className="min-h-screen text-text-primary">
      <SEO title={PAGE_META['/officers'].title} description={PAGE_META['/officers'].description} path="/officers" />
      <UnifiedHeader variant="heritage" currentPage="Meet the Officers" />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/40 z-10"></div>
          <div
            className="w-full h-full bg-cover transform scale-110"
            style={{
              backgroundImage: "url('/images/family-hero.jpeg')",
              backgroundPosition: '50% 80%',
              filter: 'blur(0.5px)'
            }}
          ></div>
        </div>

        <div className="relative z-20 text-center max-w-4xl px-4">
          <p className="text-white/80 text-sm font-medium mb-4">Our Leadership Team</p>
          <h1 className="text-4xl md:text-6xl font-display font-semibold mb-6 text-white leading-tight tracking-wide" style={{
            textShadow: '2px 2px 6px rgba(0,0,0,0.8)'
          }}>
            Meet the Officers
          </h1>
          <p className="text-lg text-white/90 leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
            Dedicated leaders who guide our family association with wisdom, integrity, and unwavering commitment to our heritage
          </p>
        </div>
      </section>

      {/* Officers Grid */}
      <section className="py-16 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-2 bg-surface-1 px-6 py-3 rounded-lg border border-border mb-8">
              <Crown className="w-5 h-5 text-brand" />
              <span className="text-text-secondary text-sm font-medium">Elected Officers</span>
            </div>

            <h2 className="text-5xl md:text-7xl font-display font-bold mb-8 text-text-primary">
              Our Leadership
            </h2>

            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Elected at the 2025 Family Reunion, our officers serve with dedication to preserve and strengthen the bonds of the Redd family
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {officers.map((officer, index) => (
              <div key={index} className="group">
                <div className="bg-surface-1 rounded-lg p-8 border border-border shadow-sm hover:shadow transition-all text-center">
                  {officer.image && (
                    <div className="relative mb-6">
                      <img
                        src={officer.image}
                        alt={`${officer.name} - ${officer.title}`}
                        className="w-24 h-24 object-cover rounded-full mx-auto border-2 border-border shadow-sm"
                      />
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-brand rounded-full flex items-center justify-center shadow-sm">
                        <officer.icon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}

                  {!officer.image && (
                    <div className="w-16 h-16 bg-brand rounded-lg flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <officer.icon className="w-8 h-8 text-white" />
                    </div>
                  )}

                  <h3 className="text-xl font-bold mb-2 text-text-primary">
                    {officer.name}
                  </h3>

                  <div className="inline-block px-3 py-1 bg-brand text-white text-sm font-semibold rounded-lg mb-4">
                    {officer.title}
                  </div>

                  <p className="text-text-secondary text-sm leading-relaxed">
                    {officer.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-surface-1 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="bg-surface-2 rounded-lg p-12 border border-border shadow-sm">
            <div className="w-20 h-20 bg-brand rounded-lg flex items-center justify-center mx-auto mb-8 shadow-sm">
              <Users className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-4xl font-display font-bold mb-6 text-text-primary">
              Learn More About Us
            </h2>

            <p className="text-lg text-text-secondary mb-8 leading-relaxed max-w-2xl mx-auto">
              Discover our family's heritage, mission, and the values that unite descendants of David Benjamin and Patience Redd across generations.
            </p>

            <Link
              to="/about"
              className="bg-brand hover:bg-brand-dark text-white px-12 py-4 rounded-lg text-xl font-semibold shadow-sm transition-all inline-flex items-center space-x-3"
            >
              <Heart className="w-6 h-6" />
              <span>About the Redd Family</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MeetTheOfficersPage;
